import React from 'react';
import { Tooltip } from '../Tooltip';
import { SectionIntro } from '../SectionIntro';
import { formatInt, cleanInt, handleIntInputFocus, handleIntInputBlur } from '../../utils/formatters';
export const ModelMarketFit = ({
  tam,
  captureRate,
  updateSimulation,
  projectedRevenue
}) => {
  const formatRevenue = value => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value}`;
  };
  const handleTamChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = cleanInt(e.target.value);
    if (cleaned === '') {
      updateSimulation({
        tam: 0
      });
      return;
    }
    const value = parseInt(cleaned);
    if (!isNaN(value)) {
      updateSimulation({
        tam: value
      });
    }
  };
  return <div className="space-y-6" data-id="element-148">
      <SectionIntro title="Model ↔ Market Fit" description="Can your business model capture enough of the market to reach your revenue goals? This is where TAM (Total Addressable Market) meets your pricing and determines if $100M+ is realistic." whyItMatters="A great product with strong unit economics still fails if the market is too small. You need enough potential customers at your price point to hit your revenue targets. This is the reality check on your ambitions." data-id="element-149" />
      <div className="space-y-2" data-id="element-150">
        <label className="flex items-center text-sm text-gray-300" data-id="element-151">
          Total Addressable Market (TAM)
          <Tooltip title="Total Addressable Market" description="How many potential customers exist for your product? Be realistic—don't count everyone who 'might' need it. Count those who have the problem you solve, can afford your solution, and are actively looking for alternatives. TAM × Capture Rate × ARPU = Your Revenue Ceiling." learnMoreUrl="https://www.reforge.com/blog/how-to-build-a-growth-model" data-id="element-152" />
        </label>
        <input type="text" inputMode="numeric" data-int-field="true" defaultValue={formatInt(tam)} onChange={handleTamChange} onFocus={handleIntInputFocus} onBlur={handleIntInputBlur} className="w-full bg-[#0F172A] border border-[#334155] text-white py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 font-mono" data-id="element-153" />
        <div className="text-xs text-gray-400 italic mt-1" data-id="element-154">
          💡 Tip: For B2B, count companies in your ICP. For B2C, count
          addressable users.
        </div>
      </div>
      <div className="space-y-2" data-id="element-155">
        <div className="flex justify-between" data-id="element-156">
          <label className="flex items-center text-sm text-gray-300" data-id="element-157">
            Capture Rate Target
            <Tooltip title="Market Capture Rate" description="What % of the TAM can you realistically capture? 1-5% is typical for most markets. 10%+ is aggressive and requires market leadership. 20%+ is rare and usually means market dominance or a redefined niche. Be conservative—overestimating here is a common startup mistake." learnMoreUrl="https://www.reforge.com/blog/how-to-build-a-growth-model" data-id="element-158" />
          </label>
          <span className="text-sm font-mono text-white" data-id="element-159">{captureRate}%</span>
        </div>
        <div className="relative" data-id="element-160">
          <input type="range" min="1" max="20" value={captureRate} onChange={e => updateSimulation({
          captureRate: parseInt(e.target.value)
        })} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-161" />
          <div className="absolute inset-0 pointer-events-none" data-id="element-162">
            <div className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300 rounded-lg" style={{
            width: `${captureRate / 20 * 100}%`
          }} data-id="element-163"></div>
          </div>
        </div>
        <div className="text-xs text-gray-400 italic mt-1" data-id="element-164">
          {captureRate <= 5 && '💡 Conservative and realistic for most markets'}
          {captureRate > 5 && captureRate <= 10 && '💡 Ambitious—requires strong execution and market fit'}
          {captureRate > 10 && '💡 Very aggressive—typically requires market dominance'}
        </div>
      </div>
      <div className="pt-2" data-id="element-165">
        <div className="bg-[#0F172A] bg-opacity-70 p-4 rounded-lg" data-id="element-166">
          <div className="flex justify-between items-center mb-2" data-id="element-167">
            <span className="text-sm text-gray-300 flex items-center" data-id="element-168">
              Projected Annual Revenue
              <Tooltip title="Revenue Projection" description="TAM × Capture Rate × ARPU = Your potential revenue. This is your theoretical ceiling if you execute perfectly. To reach $100M, you need the right combination of market size, capture rate, and pricing." data-id="element-169" />
            </span>
            <span className={`text-2xl font-mono font-bold ${projectedRevenue >= 100000000 ? 'text-green-500' : 'text-red-500'}`} data-id="element-170">
              {formatRevenue(projectedRevenue)}
            </span>
          </div>
          {projectedRevenue >= 100000000 ? <div className="mt-2 p-2 bg-green-500 bg-opacity-20 rounded-lg text-xs text-green-400 leading-relaxed" data-id="element-171">
              ✅ Your model can support $100M+ revenue. Now it's about execution
              and achieving your capture rate targets.
            </div> : <div className="mt-2 p-2 bg-red-500 bg-opacity-20 rounded-lg" data-id="element-172">
              <div className="text-xs font-semibold text-red-400 mb-2" data-id="element-173">
                ⚠️ Below $100M Target
              </div>
              <div className="text-xs text-gray-300 leading-relaxed space-y-1" data-id="element-174">
                <div data-id="element-175">
                  Your current model caps out at{' '}
                  {formatRevenue(projectedRevenue)}. To reach $100M, you need
                  to:
                </div>
                <ul className="list-disc list-inside ml-2 mt-1 space-y-0.5" data-id="element-176">
                  <li data-id="element-177">
                    Expand your TAM (new markets, verticals, or use cases)
                  </li>
                  <li data-id="element-178">Increase ARPU (add premium tiers or expand value)</li>
                  <li data-id="element-179">
                    Improve capture rate (better product, sales, or marketing)
                  </li>
                </ul>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};